<script lang="ts">
  import { createEventDispatcherDebounced } from '$lib/utils/debounce';
  import { onMount } from 'svelte';
  const dispatch = createEventDispatcherDebounced<{ change: { min: number; max: number } }>(500);

  export let min = 0;
  export let max = 100;
  export let rangeColor = '#EA0639';
  export let sliderColor = '#DBE0E6';
  export let format = (value: number) => value.toString();

  export let currentMin = min;
  export let currentMax = max;

  let fromSlider: HTMLInputElement;
  let fromLabel: HTMLDivElement;
  let toSlider: HTMLInputElement;
  let toLabel: HTMLDivElement;

  function update() {
    if (!fromSlider || !toSlider) return;

    const rangeDistance = max - min;
    const fromPosition = currentMin - min;
    const toPosition = currentMax - min;
    const bg = `linear-gradient(
      to right,
      ${sliderColor} 0%,
      ${sliderColor} ${(fromPosition / rangeDistance) * 100}%,
      ${rangeColor} ${(fromPosition / rangeDistance) * 100}%,
      ${rangeColor} ${(toPosition / rangeDistance) * 100}%, 
      ${sliderColor} ${(toPosition / rangeDistance) * 100}%, 
      ${sliderColor} 100%)`;
    fromSlider.style.background = bg;
    toSlider.style.background = bg;

    fromSlider.value = currentMin.toString();
    toSlider.value = currentMax.toString();

    const fromLabelLeftPercentage = (fromPosition / rangeDistance) * 100;
    const toLabelLeftPercentage = (toPosition / rangeDistance) * 100;
    const xDistance = toLabelLeftPercentage - fromLabelLeftPercentage;
    if (xDistance < 8) {
      toLabel.style.opacity = '0';

      if (currentMin === currentMax) {
        fromLabel.innerText = format(currentMin);
      } else {
        fromLabel.innerText = format(currentMin) + ' - ' + format(currentMax);
      }
      const middlePosition = (fromPosition + toPosition) / 2;
      const middleLabelLeftPercentage = (middlePosition / rangeDistance) * 100;
      const middleLabelLeftRem = (middlePosition / rangeDistance - 0.5) * 1.667;
      fromLabel.style.left = `calc(${middleLabelLeftPercentage}% - ${middleLabelLeftRem}rem)`;
    } else {
      toLabel.style.opacity = '1';

      const fromLabelLeftRem = (fromPosition / rangeDistance - 0.5) * 1.667;
      fromLabel.style.left = `calc(${fromLabelLeftPercentage}% - ${fromLabelLeftRem}rem)`;
      fromLabel.innerText = format(currentMin);

      const toLabelLeftRem = (toPosition / rangeDistance - 0.5) * 1.667;
      toLabel.style.left = `calc(${toLabelLeftPercentage}% - ${toLabelLeftRem}rem)`;
      toLabel.innerText = format(currentMax);
    }
  }

  function fromChanged(event: Event) {
    const newMin = parseInt((event.target as HTMLInputElement).value);
    if (newMin < currentMax) {
      currentMin = newMin;
    } else {
      currentMin = currentMax - 1;
      fromSlider.value = currentMin.toString();
    }
    dispatch('change', { min: currentMin, max: currentMax });
  }

  function toChanged(event: Event) {
    const newMax = parseInt((event.target as HTMLInputElement).value);
    if (newMax > currentMin) {
      currentMax = newMax;
    } else {
      currentMax = currentMin + 1;
      toSlider.value = currentMax.toString();
    }
    dispatch('change', { min: currentMin, max: currentMax });
  }

  onMount(() => {
    update();
    fromSlider.value = currentMin.toString();
    toSlider.value = currentMax.toString();
  });

  $: currentMin, currentMax, update();
</script>

<fieldset>
  <input type="range" on:input={fromChanged} {min} {max} bind:this={fromSlider} />
  <input type="range" on:input={toChanged} {min} {max} bind:this={toSlider} />
  <div class="from-label" bind:this={fromLabel}></div>
  <div class="to-label" bind:this={toLabel}></div>
</fieldset>

<style lang="scss">
  @import '../../../variables.scss';

  fieldset {
    position: relative;
    min-height: 50px;
    width: 100%;
    max-width: 45rem;
  }

  .from-label,
  .to-label {
    position: absolute;
    top: 1.5rem;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    line-height: 1;
    color: $black-coral;
    font-weight: 500;
    font-size: 1.167rem;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    pointer-events: all;
    width: 1.667rem;
    height: 1.667rem;
    background-color: #fff;
    border-radius: 50%;
    border: 2px solid $magic-potion;
    cursor: pointer;
  }

  input[type='range']::-moz-range-thumb {
    -webkit-appearance: none;
    pointer-events: all;
    width: 1.167rem;
    height: 1.167rem;
    background-color: #fff;
    border-radius: 50%;
    border: 2px solid $magic-potion;
    cursor: pointer;
  }

  input[type='range']::-webkit-slider-thumb:hover {
    background: #f7f7f7;
  }

  input[type='range']::-webkit-slider-thumb:active {
    box-shadow: inset 0 0 3px #387bbe, 0 0 9px #387bbe;
    -webkit-box-shadow: inset 0 0 3px #387bbe, 0 0 9px #387bbe;
  }

  input[type='range'] {
    -webkit-appearance: none;
    appearance: none;
    height: 2px;
    width: 100%;
    top: 0;
    position: absolute;
    background-color: #c6c6c6;
    pointer-events: none;
  }

  input[type='range']:first-of-type {
    height: 0;
    z-index: 1;
  }

  input[type='range']:last-of-type {
    margin-top: -1px;
  }
</style>
